/**
 * useAnalyzeHandler Hook
 *
 * Encapsulates the complex analyze submission logic including:
 * - Debounce protection against double-tap submissions
 * - Pre-flight quota validation
 * - Turnstile verification for anonymous users
 * - Analytics tracking
 * - Navigation to animation or inline results
 *
 * Extracted from index.tsx to reduce complexity and improve testability.
 */
import { useCallback, useEffect, useRef } from "react";
import { Platform } from "react-native";
import { useRouter } from "expo-router";
import { useTranslation } from "@dramadetector/shared";
import { captureEvent } from "../lib/analytics";
import { useTurnstileEnabledFlag } from "./useFeatureFlags";
import { requiresAnonymousTurnstileVerification } from "../lib/turnstile/anonymousTurnstileGate";
import type { TheatricalModeVariant } from "../lib/theatrical/theatricalModeConfig";
import type { UniversalQuotaData } from "./quota/quotaTypes";
/** Animation speed preset (hardcoded per plan requirements) */
const ANIMATION_SPEED = "1.25";
/** Animation variant (hardcoded per plan requirements) */
const ANIMATION_VARIANT = "letterZoom";
/** Platform type cast once at module level */
const PLATFORM_TYPE = Platform.OS as "ios" | "android" | "web";

interface UseAnalyzeHandlerOptions {
  /** Current message text */
  message: string;
  /** Current context selection */
  context: string;
  /** Validation function that returns true if message is valid */
  validateMessage: () => boolean;
  /** Whether user is authenticated (has valid access token) */
  isAuthenticated: boolean;
  /** Quota data for pre-flight checks */
  quotaData: UniversalQuotaData | null;
  /** Whether user prefers reduced motion (accessibility) */
  prefersReducedMotion: boolean;
  /** Current theatrical mode variant */
  theatricalVariant: TheatricalModeVariant;
  /** Turnstile token for anonymous users */
  turnstileToken: string | null;
  /** DRAMA-WEB-28: clearance token (skips Turnstile) */
  clearanceToken: string | null;
  /** Request Turnstile widget reset to prefetch next token (Phase 2) */
  requestTurnstileReset: () => void;
  /** Function to set turnstile error */
  setTurnstileError: (error: string | null) => void;
  /** Analyze mutation function - returns store ID for navigation */
  analyze: (text: string, context: string, token?: string, clearanceToken?: string) => string;
}

interface UseAnalyzeHandlerResult {
  /** Debounced analyze handler */
  handleAnalyze: () => void;
}

/**
 * Hook that manages the analyze submission flow with debouncing and validation.
 */
export function useAnalyzeHandler(
  options: UseAnalyzeHandlerOptions
): UseAnalyzeHandlerResult {
  const {
    message,
    context,
    validateMessage,
    isAuthenticated,
    quotaData,
    prefersReducedMotion,
    theatricalVariant,
    turnstileToken,
    clearanceToken,
    requestTurnstileReset,
    setTurnstileError,
    analyze,
  } = options;

  const { t } = useTranslation("home");
  const router = useRouter();
  const turnstileEnabled = useTurnstileEnabledFlag();

  // Debounce timeout ref to prevent double-tap quota deduction
  // Root cause: 50ms timing window between button press and isPending state update
  // Solution: 500ms debounce lock (industry standard for preventing double-clicks)
  const analyzeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAnonymous = !isAuthenticated;

  const handleAnalyze = useCallback(() => {
    // Debounce lock - prevent duplicate submissions within 500ms
    if (analyzeTimeoutRef.current) {
      console.warn(
        "[useAnalyzeHandler] Debounce: Ignoring duplicate analyze call within 500ms"
      );
      return;
    }

    // Set debounce lock for 500ms
    analyzeTimeoutRef.current = setTimeout(() => {
      analyzeTimeoutRef.current = null;
    }, 500);

    if (!validateMessage()) return;

    // E2E Test Mode: Skip quota checks for automated testing
    const isE2ETestMode = process.env.EXPO_PUBLIC_E2E_TEST_MODE === "true";

    // Pre-flight quota check (defense in depth)
    // Backend also enforces quota, this provides instant UX without wasted API call
    if (
      !isE2ETestMode &&
      quotaData &&
      quotaData.remaining <= 0 &&
      (quotaData.tier === "free" || quotaData.tier === "anonymous")
    ) {
      // NocoDB #792: Trial_LimitReached - fires when user hits quota wall
      captureEvent("Trial_LimitReached", {
        user_tier: quotaData.tier,
        quota_limit: quotaData.limit,
        analysis_count: quotaData.used,
      });
      // Legacy event for backward-compat dashboards
      captureEvent("quota_paywall_shown", {
        trigger: "analyze_attempt",
        quota_remaining: 0,
        quota_limit: quotaData.limit,
        quota_used: quotaData.used,
        reset_at: quotaData.resetAt,
        tier: quotaData.tier,
      });

      if (isAnonymous) {
        // Anonymous users cannot access /(tabs)/subscription (auth-protected).
        // Route directly to login so they can create an account.
        router.push("/(auth)/login");
      } else {
        router.push({
          pathname: "/(tabs)/subscription",
          params: { source: "quota_exceeded" },
        });
      }
      return;
    }

    // DRAMA-WEB-28: clearanceToken is an alternative to turnstileToken
    // Skip Turnstile requirement when feature flag is OFF
    if (
      requiresAnonymousTurnstileVerification({
        isAnonymous,
        turnstileEnabled,
        turnstileToken,
        clearanceToken,
      }) &&
      !isE2ETestMode
    ) {
      setTurnstileError(t("home.verification_required"));
      return;
    }

    const textToAnalyze = message.trim();

    // DRAMA-WEB-28: clearanceToken skips Turnstile on backend
    const storeId = analyze(
      textToAnalyze,
      context,
      isAnonymous ? turnstileToken || undefined : undefined,
      isAnonymous ? clearanceToken || undefined : undefined
    );

    // DRAMA-WEB-28: skip Turnstile reset when clearance token exists
    if (isAnonymous && !clearanceToken) {
      requestTurnstileReset();
    }

    // For reduced motion users, skip animation phase entirely
    if (prefersReducedMotion) {
      captureEvent("unified_analysis_started", {
        speed: "static",
        variant: "reduced_motion",
        text_length: textToAnalyze.length,
        platform: PLATFORM_TYPE,
      });
      // Results will show inline via existing useEffect
    } else {
      // Navigate to animation route
      router.push({
        pathname: "/analyze/[id]",
        params: {
          id: storeId,
          mode: theatricalVariant,
          text: textToAnalyze,
        },
      });

      captureEvent("unified_analysis_started", {
        speed: ANIMATION_SPEED,
        variant: ANIMATION_VARIANT,
        text_length: textToAnalyze.length,
        theatrical_mode: theatricalVariant,
        platform: PLATFORM_TYPE,
      });
    }
  }, [
    validateMessage,
    message,
    context,
    isAuthenticated,
    quotaData,
    router,
    isAnonymous,
    turnstileEnabled,
    turnstileToken,
    clearanceToken,
    requestTurnstileReset,
    setTurnstileError,
    prefersReducedMotion,
    analyze,
    t,
    theatricalVariant,
  ]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (analyzeTimeoutRef.current) {
        clearTimeout(analyzeTimeoutRef.current);
      }
    };
  }, []);

  return { handleAnalyze };
}
